import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Button, Card, CardContent, List, ListItem,
  ListItemIcon, ListItemText, Typography, useTheme,
  IconButton
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowLeftRounded, ArrowRightRounded,
  ArrowRightOutlined, CheckCircle
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const MotionBox = motion(Box);
const MotionCard = motion(Card);
const MotionImg = motion('img');

const SlideImage = ({ payload = [] }) => {
  const theme = useTheme();
  const [index, setIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  
  const len = payload.length;

  const handleNextIndex = useCallback(() => {
    setIndex((i) => (i >= len - 1 ? 0 : i + 1));
  }, [len]);

  const handlePrevIndex = useCallback(() => {
    setIndex((i) => (i <= 0 ? len - 1 : i - 1));
  }, [len]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNextIndex();
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevIndex();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleNextIndex, handlePrevIndex]);

  const [slideDirection, setSlideDirection] = useState(0);

  const changeSlide = useCallback((newIndex) => {
    const direction = newIndex > index ? 1 : -1;
    setSlideDirection(direction);
    setIndex(newIndex);
    setIsLoading(true);
  }, [index]);

  if (!payload.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
        <Typography variant="h6">No slides available</Typography>
      </Box>
    );
  }

  const item = payload[index];

  return (
    <Box
      sx={{
        py: { xs: 4, md: 6 },
        maxWidth: 1400,
        mx: 'auto',
        px: { xs: 2, md: 4 }
      }}
    >
      {/* Main Image Display - Large and Centered */}
      <Box
        sx={{
          position: 'relative',
          borderRadius: 3,
          overflow: 'hidden',
          mb: 4,
          border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          minHeight: 700,
          bgcolor: 'background.default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {/* Image with slide animation */}
        <Box sx={{ position: 'relative', width: '100%', height: '100%', minHeight: 700 }}>
          <AnimatePresence mode="wait" custom={slideDirection}>
            <MotionImg
              key={`img-${index}`}
              src={item.link}
              alt={item.title}
              custom={slideDirection}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onLoad={() => setIsLoading(false)}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                position: 'absolute',
                cursor: 'pointer'
              }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            />
          </AnimatePresence>

          {/* Loading overlay */}
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.default'
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Loading image...
              </Typography>
            </Box>
          )}

          {/* Navigation Arrows - Only show if multiple slides */}
          {len > 1 && (
            <>
              <IconButton
                onClick={handlePrevIndex}
                sx={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: alpha(theme.palette.background.paper, 0.9),
                  backdropFilter: 'blur(8px)',
                  '&:hover': { 
                    bgcolor: theme.palette.background.paper,
                    transform: 'translateY(-50%) scale(1.1)'
                  },
                  transition: 'all 0.2s ease',
                  width: 56,
                  height: 56
                }}
              >
                <ArrowLeftRounded fontSize="large" />
              </IconButton>

              <IconButton
                onClick={handleNextIndex}
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: alpha(theme.palette.background.paper, 0.9),
                  backdropFilter: 'blur(8px)',
                  '&:hover': { 
                    bgcolor: theme.palette.background.paper,
                    transform: 'translateY(-50%) scale(1.1)'
                  },
                  transition: 'all 0.2s ease',
                  width: 56,
                  height: 56
                }}
              >
                <ArrowRightRounded fontSize="large" />
              </IconButton>
            </>
          )}

          {/* Slide Indicator Dots */}
          {len > 1 && (
            <Box 
              sx={{ 
                position: 'absolute', 
                bottom: 24, 
                left: 0, 
                right: 0,
                display: 'flex',
                justifyContent: 'center',
                gap: 1.5
              }}
            >
              {payload.map((_, i) => (
                <Box
                  key={i}
                  onClick={() => changeSlide(i)}
                  sx={{
                    width: index === i ? 32 : 12,
                    height: 12,
                    borderRadius: 6,
                    bgcolor: index === i ? 
                      theme.palette.primary.main : 
                      alpha(theme.palette.common.white, 0.5),
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: index === i ? 
                        theme.palette.primary.dark : 
                        alpha(theme.palette.common.white, 0.8),
                      transform: 'scale(1.2)'
                    }
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Text Content Card at Bottom */}
      <MotionCard
        key={`card-${index}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        sx={{
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          {/* Title */}
          <Typography 
            variant="h4" 
            fontWeight={700} 
            gutterBottom
            sx={{ 
              color: 'primary.main',
              mb: 3
            }}
          >
            {item.title}
          </Typography>

          {/* Features List */}
          <List disablePadding>
            {(item.features || []).map((text, idx) => (
              <ListItem
                key={idx}
                sx={{ 
                  py: 1.5,
                  px: 0,
                  borderBottom: idx < (item.features?.length - 1) ? 
                    `1px solid ${alpha(theme.palette.divider, 0.5)}` : 'none'
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <CheckCircle color="success" sx={{ fontSize: 24 }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                      {text}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>

          {/* Slide Counter */}
          {len > 1 && (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mt: 4,
                pt: 3,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Slide {index + 1} of {len}
              </Typography>
              
              {/* Navigation Buttons */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={handlePrevIndex}
                  startIcon={<ArrowLeftRounded />}
                  variant="outlined"
                  size="large"
                  disabled={len <= 1}
                  sx={{ 
                    borderRadius: 2,
                    px: 3,
                    py: 1
                  }}
                >
                  Previous
                </Button>
                <Button
                  onClick={handleNextIndex}
                  endIcon={<ArrowRightRounded />}
                  variant="contained"
                  size="large"
                  disabled={len <= 1}
                  sx={{ 
                    borderRadius: 2,
                    px: 3,
                    py: 1
                  }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </MotionCard>
    </Box>
  );
};

export default SlideImage;